import * as Sentry from '@sentry/react';
import { isIosDevice } from './captureLimits';

/**
 * Which camera takes the photo.
 *
 * native: <input capture> hands over to the phone's camera app. On Android that
 * puts Chrome in the background, where a low-memory phone kills it and the
 * photo never reaches the page.
 *
 * inpage: a getUserMedia viewfinder and ImageCapture.takePhoto(), so Chrome
 * never leaves the screen. Chromium's takePhoto returns the sensor's full
 * resolution; WebKit's is capped at the video track's, so iOS keeps the
 * native camera, as does any browser without ImageCapture (Firefox).
 */
export type CameraMode = 'native' | 'inpage';

const FORCED_KEY = 'camera-mode';
const FALLBACK_KEY = 'camera-mode-fallback';

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
}

function isMode(value: string | null): value is CameraMode {
  return value === 'native' || value === 'inpage';
}

/** `?camera=native|inpage` forces a mode for this tab, to compare both on one phone. */
function forcedMode(): CameraMode | null {
  const fromUrl = new URLSearchParams(window.location.search).get('camera');
  if (isMode(fromUrl)) {
    writeSession(FORCED_KEY, fromUrl);
    return fromUrl;
  }
  const stored = readSession(FORCED_KEY);
  return isMode(stored) ? stored : null;
}

function supportsInPageCamera(): boolean {
  if (isIosDevice(navigator.userAgent, navigator.maxTouchPoints ?? 0)) return false;
  return 'ImageCapture' in window && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export function cameraMode(): CameraMode {
  if (typeof window === 'undefined') return 'native';
  const forced = forcedMode();
  if (forced) return forced;
  if (readSession(FALLBACK_KEY)) return 'native';
  // Fleet-wide off switch: set VITE_CAMERA_MODE=native in Cloudflare Pages and redeploy.
  if (import.meta.env.VITE_CAMERA_MODE === 'native') return 'native';
  return supportsInPageCamera() ? 'inpage' : 'native';
}

/** The viewfinder would not start here. The native camera takes over for the rest of the tab. */
export function fallBackToNativeCamera(): void {
  writeSession(FALLBACK_KEY, '1');
  Sentry.setTag('camera_mode', 'native');
}
